import React, { useState, useMemo, useCallback } from 'react';
import JSZip from 'jszip';
import piexif from 'piexifjs';

/**
 * Transform a Cloudinary URL to a specific width for thumbnails or downloads
 */
const cloudinaryTransform = (url, width, quality = 'auto') => {
  if (!url) return url;
  // Replace existing w_XXXX,q_auto with desired params
  return url.replace(/w_\d+,q_auto/, `w_${width},c_limit,q_${quality}`);
};

/**
 * Get thumbnail URL (400px) from an image URL
 */
const thumbnailUrl = (url) => cloudinaryTransform(url, 400);

/**
 * Get download URL (1200px max, forced JPEG for EXIF support) from an image URL
 */
const downloadUrl = (url) => {
  if (!url) return url;
  // Force JPEG format (f_jpg) for EXIF compatibility - Cloudinary may return WebP otherwise
  // URL format: .../upload/q_auto,w_1920/v1/... -> .../upload/w_1200,q_90,f_jpg/v1/...
  return url.replace(
    /\/upload\/[^/]+\//,
    '/upload/w_1200,c_limit,q_90,f_jpg/'
  );
};

/**
 * Sanitize a string for use in filenames
 */
const sanitizeFilename = (str) =>
  str
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .toLowerCase()
    .slice(0, 50);

/**
 * Format date as YYYY-MM-DD
 */
const formatDate = (dateStr) => {
  if (!dateStr) return 'unknown-date';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return 'unknown-date';
  return d.toISOString().split('T')[0];
};

/**
 * Build filename for a downloaded image
 */
const buildFilename = (eventName, eventStart, index) => {
  const name = sanitizeFilename(eventName || 'event');
  const date = formatDate(eventStart);
  const num = String(index + 1).padStart(3, '0');
  return `${name}_${date}_${num}.jpg`;
};

/**
 * Format date for EXIF DateTimeOriginal (YYYY:MM:DD HH:MM:SS)
 */
const formatExifDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}:${pad(d.getMonth() + 1)}:${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

/**
 * Add EXIF metadata to a JPEG blob
 * @param {Blob} blob - The original JPEG blob
 * @param {Object} metadata - Metadata to embed
 * @param {string} metadata.eventName - Event/pipe name
 * @param {string} metadata.userName - Username who posted
 * @param {number} [metadata.userAge] - Age of the user (if available)
 * @param {string} metadata.createdAt - ISO date string when posted
 * @param {string} metadata.eventUrl - URL to the replay
 * @returns {Promise<Blob>} - New blob with EXIF data
 */
const addExifToBlob = async (blob, metadata) => {
  try {
    console.log('[EXIF] Adding metadata:', metadata);

    // Convert blob to data URL
    const dataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });

    console.log('[EXIF] Data URL length:', dataUrl.length);

    // Try to load existing EXIF, or create empty structure
    let exifObj;
    try {
      exifObj = piexif.load(dataUrl);
      console.log('[EXIF] Loaded existing EXIF');
    } catch (loadErr) {
      console.log('[EXIF] No existing EXIF, creating new:', loadErr.message);
      exifObj = { '0th': {}, Exif: {}, GPS: {}, Interop: {}, '1st': {} };
    }

    // Build EXIF data
    const { eventName, userName, userAge, createdAt, eventUrl } = metadata;

    // 0th IFD (main image info)
    if (eventName) {
      exifObj['0th'][piexif.ImageIFD.ImageDescription] = eventName;
    }
    // Format artist as "Pipeup User {username}" with optional age
    if (userName) {
      const artistParts = [`Pipeup User ${userName}`];
      if (userAge) {
        artistParts.push(`(${userAge})`);
      }
      exifObj['0th'][piexif.ImageIFD.Artist] = artistParts.join(' ');
    }
    exifObj['0th'][piexif.ImageIFD.Software] = 'Pipeup';
    exifObj['0th'][piexif.ImageIFD.Copyright] = 'Copyright regulated by terms of use: www.pipeup.live/terms';

    // Exif IFD (detailed photo info)
    const exifDate = formatExifDate(createdAt);
    if (exifDate) {
      exifObj.Exif[piexif.ExifIFD.DateTimeOriginal] = exifDate;
      exifObj.Exif[piexif.ExifIFD.DateTimeDigitized] = exifDate;
    }
    // Build user comment with event info, URL, and age if available
    const commentParts = [`Event: ${eventName || 'Unknown'}`];
    if (userName) {
      commentParts.push(`User: ${userName}${userAge ? ` (${userAge})` : ''}`);
    }
    if (eventUrl) {
      commentParts.push(`URL: ${eventUrl}`);
    }
    exifObj.Exif[piexif.ExifIFD.UserComment] = `ASCII\0\0\0${commentParts.join(' | ')}`;

    // Dump EXIF to binary and insert into image
    console.log('[EXIF] Dumping EXIF object:', Object.keys(exifObj));
    const exifBytes = piexif.dump(exifObj);
    console.log('[EXIF] EXIF bytes length:', exifBytes.length);
    const newDataUrl = piexif.insert(exifBytes, dataUrl);
    console.log('[EXIF] New data URL length:', newDataUrl.length);

    // Convert data URL back to blob
    const base64 = newDataUrl.split(',')[1];
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    console.log('[EXIF] Successfully added EXIF data');
    return new Blob([bytes], { type: 'image/jpeg' });
  } catch (err) {
    console.error('[EXIF] Failed to add EXIF data:', err);
    return blob; // Return original blob if EXIF insertion fails
  }
};

/**
 * ImageGallery - browse and download snap photos from a replay
 */
const ImageGallery = ({ data }) => {
  const { posts, eventStart, config } = data;
  const eventName = config?.pipeName || 'event';

  // Filter snap images
  const snapImages = useMemo(
    () =>
      (posts || []).filter(
        (post) => post.type === 'snap' && post.imageUrl
      ),
    [posts]
  );

  const [selected, setSelected] = useState(new Set());
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const toggleSelect = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelected(new Set(snapImages.map((p) => p._id)));
  }, [snapImages]);

  const selectNone = useCallback(() => {
    setSelected(new Set());
  }, []);

  const handleDownload = useCallback(async () => {
    const selectedImages = snapImages.filter((p) => selected.has(p._id));
    if (selectedImages.length === 0) return;

    setDownloading(true);
    setProgress({ current: 0, total: selectedImages.length });

    // Get replay URL for EXIF metadata
    const eventUrl = window.location.href.split('/gallery')[0];

    try {
      if (selectedImages.length === 1) {
        // Single file download
        const img = selectedImages[0];
        const url = downloadUrl(img.imageUrl);
        const response = await fetch(url);
        let blob = await response.blob();
        const idx = snapImages.indexOf(img);
        const filename = buildFilename(eventName, eventStart, idx);

        // Add EXIF metadata
        blob = await addExifToBlob(blob, {
          eventName,
          userName: img.userName,
          userAge: img.userAge,
          createdAt: img.createdAt || eventStart,
          eventUrl,
        });

        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
        setProgress({ current: 1, total: 1 });
      } else {
        // ZIP download
        const zip = new JSZip();

        for (let i = 0; i < selectedImages.length; i++) {
          const img = selectedImages[i];
          const url = downloadUrl(img.imageUrl);
          const response = await fetch(url);
          let blob = await response.blob();
          const idx = snapImages.indexOf(img);
          const filename = buildFilename(eventName, eventStart, idx);

          // Add EXIF metadata
          blob = await addExifToBlob(blob, {
            eventName,
            userName: img.userName,
            userAge: img.userAge,
            createdAt: img.createdAt || eventStart,
            eventUrl,
          });

          zip.file(filename, blob);
          setProgress({ current: i + 1, total: selectedImages.length });
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const name = sanitizeFilename(eventName);
        const date = formatDate(eventStart);
        const zipFilename = `${name}_${date}_photos.zip`;

        const a = document.createElement('a');
        a.href = URL.createObjectURL(zipBlob);
        a.download = zipFilename;
        a.click();
        URL.revokeObjectURL(a.href);
      }
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  }, [selected, snapImages, eventName, eventStart]);

  // Navigate back to replay
  const goBack = () => {
    const path = window.location.pathname;
    const shortCode = path.replace(/^\//, '').split('/')[0];
    window.location.href = `/${shortCode}`;
  };

  // Empty state
  if (snapImages.length === 0) {
    return (
      <div className="min-h-dvh bg-black text-white flex flex-col items-center justify-center p-8">
        <div className="text-4xl mb-4">:(</div>
        <div className="text-lg mb-6">No photos found in this replay</div>
        <button
          onClick={goBack}
          className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
        >
          Back to Replay
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-black text-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-black/90 backdrop-blur-sm border-b border-white/10 px-4 py-3">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={goBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Back to Replay"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 256 256" fill="currentColor">
                <path d="M224,128a8,8,0,0,1-8,8H59.31l58.35,58.34a8,8,0,0,1-11.32,11.32l-72-72a8,8,0,0,1,0-11.32l72-72a8,8,0,0,1,11.32,11.32L59.31,120H216A8,8,0,0,1,224,128Z" />
              </svg>
            </button>
            <h1 className="text-lg font-bold truncate">{eventName}</h1>
            <span className="text-sm text-gray-400">
              {snapImages.length} photo{snapImages.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={selectAll}
              className="px-3 py-1.5 text-sm bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              Select All
            </button>
            <button
              onClick={selectNone}
              className="px-3 py-1.5 text-sm bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              disabled={selected.size === 0}
            >
              Select None
            </button>
            <button
              onClick={handleDownload}
              disabled={selected.size === 0 || downloading}
              className="px-4 py-1.5 text-sm bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg transition-colors font-medium"
            >
              {downloading
                ? `${progress.current}/${progress.total}`
                : selected.size === 0
                  ? 'Download'
                  : selected.size === 1
                    ? 'Download (1)'
                    : `Download ZIP (${selected.size})`}
            </button>
          </div>
        </div>
      </div>

      {/* Image Grid - masonry via CSS columns */}
      <div className="max-w-6xl mx-auto p-4">
        <div className="columns-2 sm:columns-3 lg:columns-4 gap-3">
          {snapImages.map((post, index) => {
            const isSelected = selected.has(post._id);
            return (
              <div
                key={post._id}
                className="relative rounded-lg overflow-hidden cursor-pointer group mb-3 break-inside-avoid"
                onClick={() => toggleSelect(post._id)}
              >
                <img
                  src={thumbnailUrl(post.imageUrl)}
                  alt={post.userName ? `Photo by ${post.userName}` : `Photo ${index + 1}`}
                  className="w-full h-auto block"
                  loading="lazy"
                />

                {/* Selection overlay */}
                <div
                  className={`absolute inset-0 transition-colors ${
                    isSelected ? 'bg-green-500/20' : 'group-hover:bg-black/20'
                  }`}
                />

                {/* Checkbox */}
                <div
                  className={`absolute top-2 right-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    isSelected
                      ? 'bg-green-500 border-green-500'
                      : 'border-white/70 bg-black/30 group-hover:border-white'
                  }`}
                >
                  {isSelected && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 256 256" fill="white">
                      <path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z" />
                    </svg>
                  )}
                </div>

                {/* Username badge */}
                {post.userName && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5">
                    <span className="text-xs text-white/80 truncate block">
                      {post.userName}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ImageGallery;
